import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { FC, useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Workspace, WorkspaceVisibility } from "../../types/workspace";
import { WorkspaceDialog } from "./WorkspaceDialog/WorkspaceDialog";
import { useGetWorkspaces } from "./hooks/useGetWorkspaces";
import { getLoggedUserProfile } from "../../utils/auth";
import { createWorkspaceRows, getDefaultWorkspaceName } from "./utils";
import { AppLayout } from "../../components/layout/AppLayout";
import { ProjectsToolbar } from "./ProjectsToolbar";
import { WorkspaceFilterBy } from "@/apis/bacchus/workspace";
import { WorkspacesTable } from "./WorkspacesTable/WorkspacesTable";
import { WorkspacesTableRowDefinition } from "./types";
import { ActionOption } from "./WorkspacesTable/ActionCell/utils";
import {
  deleteWorkspace,
  duplicateWorkspace,
  setWorkspaceToPrivate,
  setWorkspaceToPublic,
} from "../../apis/bacchus/workspace";
import { useSnackbar } from "../../components/Snackbar";
import { DeleteDialog } from "./MyWorkspaces/DeleteDialog";
import { VisibilityDialog } from "./MyWorkspaces/VisibilityDialog";

const TAB_DESCRIPTIONS: Record<WorkspaceFilterBy, string> = {
  my: "Create and manage your market analysis projects",
  org: "Browse and access projects shared by your organization",
  all: "View all projects you have access to",
};

export const WorkspaceRepository: FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { showSuccess, showError } = useSnackbar();

  // Tab state from URL
  const tabParam = params.get("tab");
  const filterBy: WorkspaceFilterBy =
    tabParam === "org" || tabParam === "all" ? tabParam : "my";

  const { workspaces, loadWorkspaces, isLoading } = useGetWorkspaces(filterBy);

  const userProfile = getLoggedUserProfile();
  const userName = userProfile?.email?.split("@")[0];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [workspaceToEdit, setWorkspaceToEdit] = useState<
    Workspace | undefined
  >();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] =
    useState<WorkspacesTableRowDefinition>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Visibility dialog state
  const [isVisibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [workspaceToUpdateVisibility, setWorkspaceToUpdateVisibility] =
    useState<WorkspacesTableRowDefinition>();
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

  const defaultWorkspaceName = getDefaultWorkspaceName(workspaces, userName);

  const workspacesRows = useMemo<WorkspacesTableRowDefinition[]>(() => {
    return createWorkspaceRows(workspaces);
  }, [workspaces]);

  // Check for create query parameter
  useEffect(() => {
    if (params.get("create") === "true") {
      setIsDialogOpen(true);
      setWorkspaceToEdit(undefined);
      setParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("create");
        return newParams;
      });
    }
  }, [setParams, params]);

  const handleFilterByChange = useCallback(
    (newFilterBy: WorkspaceFilterBy) => {
      setParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (newFilterBy === "my") {
          newParams.delete("tab");
        } else {
          newParams.set("tab", newFilterBy);
        }
        return newParams;
      });
    },
    [setParams]
  );

  function openCreateNewDialog() {
    setIsDialogOpen(true);
    setWorkspaceToEdit(undefined);
  }

  function handleCloseDialog() {
    setIsDialogOpen(false);
    setWorkspaceToEdit(undefined);
  }

  function handleRedirectToWorkspace(
    { id }: WorkspacesTableRowDefinition,
    openConfig?: boolean
  ) {
    let url = `/projects/${id}`;
    if (openConfig) url += "/configuration";
    navigate(url);
  }

  function handleEditDetailsAction({ id }: WorkspacesTableRowDefinition) {
    const workspace = workspaces.find((workspace) => workspace.id === id);
    if (workspace) {
      setIsDialogOpen(true);
      setWorkspaceToEdit(workspace);
    }
  }

  async function handleDuplicateAction({ id }: WorkspacesTableRowDefinition) {
    const { error } = await duplicateWorkspace(id);

    if (error) {
      showError(error.message);
      return;
    }

    loadWorkspaces();
    showSuccess("The project has been successfully duplicated.");
  }

  function handleDeleteAction(row: WorkspacesTableRowDefinition) {
    setWorkspaceToDelete(row);
    setDeleteDialogOpen(true);
  }

  function handleVisibilityAction(row: WorkspacesTableRowDefinition) {
    setWorkspaceToUpdateVisibility(row);
    setVisibilityDialogOpen(true);
  }

  const handleActionClick = (
    action: ActionOption,
    row: WorkspacesTableRowDefinition
  ) => {
    switch (action) {
      case ActionOption.View:
        handleRedirectToWorkspace(row);
        break;
      case ActionOption.EditDetails:
        handleEditDetailsAction(row);
        break;
      case ActionOption.EditConfig:
        handleRedirectToWorkspace(row, true);
        break;
      case ActionOption.Duplicate:
        handleDuplicateAction(row);
        break;
      case ActionOption.SetPrivate:
      case ActionOption.SetPublic:
        handleVisibilityAction(row);
        break;
      case ActionOption.Delete:
        handleDeleteAction(row);
        break;
    }
  };

  const confirmDelete = async () => {
    if (!workspaceToDelete) return;

    setIsDeleting(true);
    const { error } = await deleteWorkspace(workspaceToDelete.id);

    if (error) {
      showError(error.message);
    } else {
      showSuccess("Project was deleted successfully");
      loadWorkspaces();
    }
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setWorkspaceToDelete(undefined);
  };

  const confirmVisibilityChange = async () => {
    if (!workspaceToUpdateVisibility) return;

    const isSettingPublic =
      workspaceToUpdateVisibility.visibilityStatus ===
      WorkspaceVisibility.PRIVATE;
    setIsUpdatingVisibility(true);
    const { error } = isSettingPublic
      ? await setWorkspaceToPublic(workspaceToUpdateVisibility.id)
      : await setWorkspaceToPrivate(workspaceToUpdateVisibility.id);

    setIsUpdatingVisibility(false);
    setVisibilityDialogOpen(false);
    if (error) {
      showError(error.message);
    } else {
      showSuccess(
        `The project has been successfully made ${isSettingPublic ? "public" : "private"}.`
      );
      loadWorkspaces();
    }
    setWorkspaceToUpdateVisibility(undefined);
  };

  return (
    <>
      <AppLayout>
        <Box mb={3} p={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontSize: "32px",
                  fontWeight: 600,
                  lineHeight: 1,
                  marginBottom: "8px",
                  color: "#1f1f1f",
                }}
              >
                Projects
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  color: "#6b7280",
                  lineHeight: 1.5,
                }}
              >
                {TAB_DESCRIPTIONS[filterBy]}
              </Typography>
            </Box>
            <Button
              data-testid="workspace-create-btn"
              variant="contained"
              size="medium"
              onClick={openCreateNewDialog}
            >
              Create Project
            </Button>
          </Box>
          <ProjectsToolbar
            filterBy={filterBy}
            onFilterByChange={handleFilterByChange}
          />
        </Box>

        <Box p="12px">
          <WorkspacesTable
            rows={workspacesRows}
            isLoading={isLoading}
            onRowClick={handleRedirectToWorkspace}
            onActionClick={handleActionClick}
            showOwner={filterBy !== "my"}
          />
        </Box>
      </AppLayout>

      <WorkspaceDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        workspaces={workspaces}
        loadMyWorkspaces={loadWorkspaces}
        workspaceToEdit={workspaceToEdit}
        defaultWorkspaceName={defaultWorkspaceName}
        isPublicView={false}
      />

      <DeleteDialog
        isOpen={deleteDialogOpen}
        workspace={workspaceToDelete}
        setIsOpen={setDeleteDialogOpen}
        onConfirmDelete={confirmDelete}
        isDeleting={isDeleting}
      />

      <VisibilityDialog
        isLoading={isUpdatingVisibility}
        isOpen={isVisibilityDialogOpen}
        workspace={workspaceToUpdateVisibility}
        setIsOpen={setVisibilityDialogOpen}
        onConfirm={confirmVisibilityChange}
      />
    </>
  );
};
